package com.yendo.mx.models

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class FinanzasPublicas(
    @SerialName("datos_bancarios") val datosBancarios: DatosBancarios? = null
)

@Serializable
data class RestauranteFirebase(
    val id: String = "",

    @SerialName("categorias_menu")
    val categoriasMenu: List<String> = emptyList(),

    @SerialName("metodos_pago_aceptados")
    val metodosPagoAceptadosRoot: List<String>? = null,

    @SerialName("finanzas_publicas")
    val finanzasPublicas: FinanzasPublicas? = null,

    // 🚀 EL CAMPO FALTANTE: Vital para que funcione el filtro "Automático" de los Carruseles
    @SerialName("etiquetasGlobales")
    val etiquetasGlobales: List<String> = emptyList(),

    @SerialName("contacto_legal")
    val contactoLegal: ContactoLegal = ContactoLegal(),

    @SerialName("finanzas_locales")
    val finanzasLocales: FinanzasLocales = FinanzasLocales(),

    @SerialName("finanzas_operativas")
    val finanzasOperativas: FinanzasOperativasConfig? = null,

    @SerialName("costo_envio")
    val costoEnvio: Double = 0.0,

    @SerialName("info_publica")
    val infoPublica: InfoPublica = InfoPublica(),

    // 🚀 BANDERAZO PRE-EVALUADO EN NUBE (0ms)
    @SerialName("esta_abierto_cloud")
    val estaAbiertoCloud: Boolean? = null,

    // 🚀 LOS HORARIOS AHORA USAN LA ESTRUCTURA FUERTE CON FRANJAS
    val horarios: Horarios? = Horarios(),

    // 🚀 EL CEREBRO DE OPERACIÓN
    @SerialName("operacion")
    val operacion: Operacion = Operacion(),

    // 🚀 LA REPUTACIÓN RECUPERADA PARA QUE NO SE PIERDAN LAS ESTRELLAS
    val reputacion: Reputacion = Reputacion(),

    @SerialName("ubicacion")
    val ubicacion: Ubicacion = Ubicacion()
) {
    val metodosPagoAceptadosFinal: List<String>
        get() = metodosPagoAceptadosRoot?.takeIf { it.isNotEmpty() }
            ?: finanzasLocales.metodosPagoAceptados.takeIf { it.isNotEmpty() }
            ?: finanzasPublicas?.datosBancarios?.let { listOf("Efectivo", "Tarjeta", "Transferencia") }
            ?: listOf("Efectivo", "Tarjeta", "Transferencia")

    val datosBancariosFinal: DatosBancarios?
        get() = finanzasLocales.datosBancarios?.takeIf { it.banco.isNotBlank() || it.cuenta.isNotBlank() }
            ?: finanzasPublicas?.datosBancarios
}

@Serializable
data class ContactoLegal(
    val email: String = "",
    @SerialName("id_identificacion_url") val idIdentificacionUrl: String = "",
    @SerialName("nombre_representante") val nombreRepresentante: String = "",
    @SerialName("telefono_negocio") val telefonoNegocio: String = "",
    @SerialName("telefono_representante") val telefonoRepresentante: String = ""
)

@Serializable
data class FinanzasLocales(
    @SerialName("comision_personalizada") val comisionPersonalizada: Int = 0,
    @SerialName("metodos_pago_aceptados") val metodosPagoAceptados: List<String> = listOf("Efectivo"),
    @SerialName("preferencia_pago_caja") val preferenciaPagoCaja: String = "NETO",
    @SerialName("datos_bancarios") val datosBancarios: DatosBancarios? = DatosBancarios(),
    @SerialName("balance_actual_deuda") val balanceActualDeuda: Double = 0.0,
    @SerialName("limite_deuda_personalizado") val limiteDeudaPersonalizado: Double? = null
)

@Serializable
data class DatosBancarios(
    val banco: String = "",
    val cuenta: String = "",
    val beneficiario: String = ""
)

@Serializable
data class InfoPublica(
    val categoria: String = "",
    val header: String = "",
    val logotipo: String = "",
    val nombre: String = "",
    @SerialName("promo_texto") val promoTexto: String = "",
    val subcategorias: List<String> = emptyList(),
    @SerialName("tiene_promo") val tienePromo: Boolean = false,

    // 🚀 LA VARIABLE MAESTRA AÑADIDA
    // Identifica si es "RESTAURANTE", "COMERCIO", o "TIENDITA"
    @SerialName("tipo_negocio")
    val tipoNegocio: String = "RESTAURANTE"
)

// 🚀 CLASE FUERTE PARA LOS DÍAS (Facilita leerlos en Compose)
@Serializable
data class Horarios(
    val lunes: List<FranjaHoraria> = emptyList(),
    val martes: List<FranjaHoraria> = emptyList(),
    val miercoles: List<FranjaHoraria> = emptyList(),
    val jueves: List<FranjaHoraria> = emptyList(),
    val viernes: List<FranjaHoraria> = emptyList(),
    val sabado: List<FranjaHoraria> = emptyList(),
    val domingo: List<FranjaHoraria> = emptyList()
)

// 🚀 ESTRUCTURA DE LA FRANJA (Lo que va dentro del Array de cada día)
@Serializable
data class FranjaHoraria(
    val abre: String = "00:00",
    val cierra: String = "23:59",
    val activo: Boolean = true // Mantenemos el switch por si luego lo usan para apagar un solo turno
)

@Serializable
data class Operacion(
    // --- CAPAS DE EMERGENCIA Y BLOQUEO ---
    @SerialName("visible") val visible: Boolean = true, // 🚀 NUEVA ADUANA DE VISIBILIDAD (ADMIN)
    @SerialName("activo_comercio") val activoComercio: Boolean = true, // 🚀 CONTROL DEL COMERCIO
    @SerialName("bloqueado_por_admin") val bloqueadoPorAdmin: Boolean = false,
    @SerialName("pausado_manualmente") val pausadoManualmente: Boolean = false,
    @SerialName("pausa_hasta") val pausaHasta: Long = 0L, // 🚀 EL TEMPORIZADOR DEL BOTÓN DE PÁNICO (Int64/Long)

    // --- LOGÍSTICA DE COMPRA ---
    @SerialName("balance_excedido") val balanceExcedido: Boolean = false,
    val estado: String = "SOLICITUD",
    @SerialName("monto_minimo_compra") val montoMinimoCompra: Int = 10,
    @SerialName("delivery_activo") val deliveryActivo: Boolean = true,
    @SerialName("delivery_propio_activo") val deliveryPropioActivo: Boolean = false,
    @SerialName("flota_propia_activa") val flotaPropiaActiva: Boolean = false,
    @SerialName("pickup_activo") val pickupActivo: Boolean = true,
    @SerialName("programado_activo") val programadoActivo: Boolean = false,

    // ==========================================
    // 🚀 NUEVA REGLA LOGÍSTICA: COBRO EN 2 PASOS
    // ==========================================
    @SerialName("pago_post_armado") val pagoPostArmado: Boolean = false
) {
    val tieneFlotaPropiaActiva: Boolean get() = deliveryPropioActivo
}

@Serializable
data class Reputacion(
    @SerialName("calificacion_promedio") val calificacionPromedio: Double = 0.0,
    @SerialName("total_reseñas") val totalResenas: Int = 0
)

@Serializable
data class Ubicacion(
    val direccion: String = "",
    @SerialName("id_zona") val idZona: String = "",
    val latitud: Double = 0.0,
    val longitud: Double = 0.0
)